/**
 * SearchBar Component for Global Search Page
 * 
 * Simplified search bar that passes input to parent immediately.
 * Debouncing is handled by useGlobalSearch hook.
 * 
 * Features:
 * - Clear button when search term is present
 * - Enter key triggers immediate search
 * - Escape key to clear
 */

import React, { useState, useCallback } from 'react';
import { Input } from 'antd';
import { SearchOutlined, CloseCircleFilled, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';

// ========================================
// Styled Components
// ========================================

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 600px;
`;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #1F2937;
  height: 44px;
  flex: 1;

  .ant-input {
    color: #1F2937;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9CA3AF;
  transition: color 0.2s;

  &:hover {
    color: #6B7280;
  }
`;

const LoadingIndicator = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563EB;
  
  .anticon {
    font-size: 16px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ========================================
// Types
// ========================================

export interface SearchBarProps {
  onSearch: (searchText: string) => void;
  isLoading?: boolean;
}

// ========================================
// Component
// ========================================

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  isLoading = false,
}) => {
  const [localValue, setLocalValue] = useState('');
  
  // Handle input change - pass to parent immediately (parent handles debounce)
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);
    onSearch(value);
  }, [onSearch]);
  
  // Handle clear
  const handleClear = useCallback(() => {
    setLocalValue('');
    onSearch('');
  }, [onSearch]);
  
  // Handle Escape key to clear
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleClear();
    }
  }, [handleClear]);
  
  return (
    <SearchContainer>
      <StyledInput
        prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
        placeholder="Search across all exhibits"
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        spellCheck={true}
        suffix={
          isLoading ? (
            <LoadingIndicator>
              <LoadingOutlined />
            </LoadingIndicator>
          ) : localValue ? (
            <ClearButton onClick={handleClear}>
              <CloseCircleFilled />
            </ClearButton>
          ) : null
        }
        allowClear={false}
      />
    </SearchContainer>
  );
};

export default SearchBar;
